import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { useMasterkasaPromotionObjects, type MasterkasaSource } from '@/hooks/use-masterkasa';
import { useT } from '@/i18n/use-t';

/**
 * RL-467 — every promotion object the store system holds, and what became of
 * it: the ones that are now Back Office promotions, and for every other one
 * each reason it is not. The import never drops an object silently; this is
 * where an operator sees that.
 */
export function MasterkasaPromotionsDialog({
  source,
  onClose,
}: {
  source: MasterkasaSource;
  onClose: () => void;
}) {
  const t = useT();
  const query = useMasterkasaPromotionObjects(source.id);
  const items = query.data?.items ?? [];

  return (
    <AlertDialog open onOpenChange={(open) => (open ? undefined : onClose())}>
      <AlertDialogContent className="max-h-[85vh] max-w-4xl overflow-y-auto">
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t('settings.integrations.masterkasa.promotions.title')} · {source.location.name}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t('settings.integrations.masterkasa.promotions.description')}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {query.isLoading ? (
          <p className="text-sm text-muted-foreground">
            {t('settings.integrations.masterkasa.loading')}
          </p>
        ) : query.isError ? (
          <p className="text-sm text-destructive">
            {t('settings.integrations.masterkasa.loadError')}
          </p>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t('settings.integrations.masterkasa.promotions.empty')}
          </p>
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>{t('settings.integrations.masterkasa.promotions.object')}</TH>
                <TH>{t('settings.integrations.masterkasa.status')}</TH>
                <TH>{t('settings.integrations.masterkasa.promotions.reasons')}</TH>
              </TR>
            </THead>
            <TBody>
              {items.map((item) => (
                <TR key={item.uid}>
                  <TD>
                    <div className="font-medium">
                      {item.name ?? t('settings.integrations.masterkasa.promotions.noObject')}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      uid {item.uid}
                      {item.note !== null && item.note !== '' ? ` · ${item.note}` : ''}
                    </div>
                  </TD>
                  <TD>
                    <Badge variant={item.status === 'mapped' ? 'success' : 'outline'}>
                      {item.status === 'mapped'
                        ? t('settings.integrations.masterkasa.promotions.mapped')
                        : t('settings.integrations.masterkasa.promotions.unmapped')}
                    </Badge>
                  </TD>
                  <TD>
                    {item.reasons.length === 0 ? (
                      '—'
                    ) : (
                      <ul className="list-disc space-y-1 pl-4 text-sm">
                        {item.reasons.map((reason) => (
                          <li key={reason}>{reason}</li>
                        ))}
                      </ul>
                    )}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel onClick={onClose}>
            {t('settings.integrations.masterkasa.promotions.close')}
          </AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
